import React, {useState, useEffect, useMemo} from 'react';
import {CardContent, Grid} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {ClientQuoteData} from '@/APIs/CRM';
import CustomerPortalAPI, {Customer} from '@/APIs/CustomerPortalAPI';
import {ParameterStore} from '../../../Core/mobx/ParameterStore/ParameterStore';

const useStyles = makeStyles({
    recommendationItem: {
        marginTop: 22,
        padding: '10px 12px 20px',
        border: '1px solid #E7E7E7',
        borderRadius: 6,
        color: '#838383',
    },
    recommendationItemTitle: {
        color: '#434343',
        fontSize: 24,
        fontWeight: 600,
        fontFamily: 'LoraSemiBold',
        lineHeight: '180%',
        textTransform: 'capitalize',
    },
});

export interface CustomerSelectedQuote {
    policyType: string;
    cashValue?: string;
    coverageAmount?: string;
    termCoverageAmount?: string;
    permCoverageAmount?: string;
    termPremium?: string;
    permPremium?: string;
    monthlyPayment?: string;
    yearCoverage?: string;
    permCarrier?: string;
    termCarrier?: string;
}

/** Props when used standalone (e.g. below Client Goals in Prequalification) */
export interface WalkthroughRecommendationSummaryStandaloneProps {
    parameterStore: ParameterStore;
    customerQuoteData?: ClientQuoteData[];
}

/** Props when used inside BestFitProduct (data already derived) */
export interface WalkthroughRecommendationSummaryInjectedProps {
    customerSelectedQuote: CustomerSelectedQuote;
    customerPortalData: Customer | null;
    walkthroughRecommendedProductType: string | undefined;
    walkthroughRecommendedQuote:
        | {faceAmount: number; modalPremium: number; surrenderValue?: number; termPeriod?: number; carrier?: string}
        | undefined;
    useProsperFlow: boolean;
    useIntentBuilderFlow: boolean;
}

export type WalkthroughRecommendationSummaryProps =
    | WalkthroughRecommendationSummaryStandaloneProps
    | (WalkthroughRecommendationSummaryInjectedProps & {parameterStore?: never; customerQuoteData?: never});

function getPremiumString(premium: number | undefined | null): string {
    if (Number.isFinite(premium))
        return `$${((premium as number) * 0.8).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} - $${((premium as number) * 1.2).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
    return 'N/A';
}

function isStandaloneProps(
    props: WalkthroughRecommendationSummaryProps,
): props is WalkthroughRecommendationSummaryStandaloneProps {
    return 'parameterStore' in props && props.parameterStore != null;
}

function deriveWalkthroughFromStore(parameterStore: ParameterStore | null) {
    if (!parameterStore)
        return {
            walkthroughRecommendedProductType: undefined,
            walkthroughRecommendedQuote: undefined,
            useProsperFlow: false,
            useIntentBuilderFlow: false,
        };
    const populateProsperFields = (quote: any, product: any) => {
        return {
            faceAmount: quote.faceAmount,
            modalPremium: quote.modalPremium,
            surrenderValue: quote.surrenderValue || quote.cashValueAt65,
            carrier: product.carrierName,
        };
    };
    // Multi-step flow may expose raw data as _walkthroughRawData
    const walkthroughRawData =
        parameterStore.walkthroughRawData ?? (parameterStore as {_walkthroughRawData?: unknown})._walkthroughRawData;
    const useProsperFlow = walkthroughRawData?.userData?.useProsperFlow === true;
    const useIntentBuilderFlow = !!walkthroughRawData?.userData?.intentBuilderResult;
    const actualWalkthroughRecommendation = walkthroughRawData?.quoteData?.productTypeRecommendation;
    let walkthroughRecommendedProductType: string | undefined =
        useProsperFlow && actualWalkthroughRecommendation === 'VUL' ? 'IUL' : actualWalkthroughRecommendation;
    let walkthroughRecommendedQuote:
        | {faceAmount: number; modalPremium: number; surrenderValue?: number; termPeriod?: number; carrier?: string}
        | undefined;

    if (useIntentBuilderFlow && walkthroughRecommendedProductType === 'IUL') {
        // If we are in the intent builder with IUL rec, default to prosper if flow was active
        // Otherwise use the regular iul quote
        if (useProsperFlow && walkthroughRawData?.quoteData?.prosper?.quote) {
            const {quote: prosperQuote, product: prosperProduct} = walkthroughRawData.quoteData.prosper;
            walkthroughRecommendedQuote = populateProsperFields(prosperQuote, prosperProduct);
        } else {
            walkthroughRecommendedQuote = walkthroughRawData?.quoteData?.iul?.quote;
            if (walkthroughRecommendedQuote) {
                walkthroughRecommendedQuote = {
                    ...walkthroughRecommendedQuote,
                    carrier: walkthroughRawData?.quoteData?.iul?.product?.carrierName,
                };
            }
        }
    } else if (!useIntentBuilderFlow && useProsperFlow && walkthroughRawData?.quoteData?.prosper?.quote) {
        // If we are not in the intent builder, default to use prosper if flow was active
        const {quote: prosperQuote, product: prosperProduct} = walkthroughRawData.quoteData.prosper;
        walkthroughRecommendedQuote = populateProsperFields(prosperQuote, prosperProduct);
        walkthroughRecommendedProductType = 'IUL';
    } else if (walkthroughRecommendedProductType === 'IUL') {
        walkthroughRecommendedQuote = walkthroughRawData?.quoteData?.iul?.quote;
        if (walkthroughRecommendedQuote) {
            walkthroughRecommendedQuote = {
                ...walkthroughRecommendedQuote,
                carrier: walkthroughRawData?.quoteData?.iul?.product?.carrierName,
            };
        }
    } else if (walkthroughRecommendedProductType === 'TERM') {
        walkthroughRecommendedQuote = walkthroughRawData?.quoteData?.term?.quote;
        if (walkthroughRecommendedQuote) {
            walkthroughRecommendedQuote = {
                ...walkthroughRecommendedQuote,
                carrier: walkthroughRawData?.quoteData?.term?.product?.carrierName,
            };
        }
    } else if (walkthroughRecommendedProductType === 'ANNUITY') {
        walkthroughRecommendedQuote = walkthroughRawData?.quoteData?.annuity?.quote;
        if (walkthroughRecommendedQuote) {
            walkthroughRecommendedQuote = {
                ...walkthroughRecommendedQuote,
                faceAmount: walkthroughRawData?.quoteData?.annuity?.quote?.cashValue,
                modalPremium: walkthroughRawData?.quoteData?.annuity?.quote?.monthlyPremium,
                carrier: walkthroughRawData?.quoteData?.annuity?.product?.carrierName,
            };
        }
    } else if (walkthroughRecommendedProductType === 'ADB') {
        walkthroughRecommendedQuote = walkthroughRawData?.quoteData?.adb?.quote;
        if (walkthroughRecommendedQuote) {
            walkthroughRecommendedQuote = {
                ...walkthroughRecommendedQuote,
                carrier: walkthroughRawData?.quoteData?.adb?.product?.carrierName,
                faceAmount: walkthroughRawData?.quoteData?.adb?.quote?.coverage,
                modalPremium: walkthroughRawData?.quoteData?.adb?.quote?.premium,
            };
        }
    } else if (walkthroughRecommendedProductType === 'FEXP') {
        walkthroughRecommendedQuote = walkthroughRawData?.quoteData?.fe?.quote;
        if (walkthroughRecommendedQuote) {
            walkthroughRecommendedQuote = {
                ...walkthroughRecommendedQuote,
                carrier: walkthroughRawData?.quoteData?.fe?.product?.carrierName,
                faceAmount: walkthroughRawData?.quoteData?.fe?.quote?.faceAmount,
                modalPremium: walkthroughRawData?.quoteData?.fe?.quote?.modalPremium,
            };
        }
    } else if (walkthroughRecommendedProductType === 'VUL') {
        walkthroughRecommendedQuote = undefined;
    }
    return {walkthroughRecommendedProductType, walkthroughRecommendedQuote, useProsperFlow, useIntentBuilderFlow};
}

export const WalkthroughRecommendationSummary: React.FC<WalkthroughRecommendationSummaryProps> = (props) => {
    const classes = useStyles();
    const isStandalone = isStandaloneProps(props);
    const parameterStore = isStandalone ? props.parameterStore : null;
    const customerQuoteDataForEffect = isStandalone ? props.customerQuoteData : undefined;

    const [standaloneCustomerSelectedQuote, setStandaloneCustomerSelected] = useState<CustomerSelectedQuote>({
        policyType: '',
        cashValue: '',
        coverageAmount: '',
        termCoverageAmount: '',
        permCoverageAmount: '',
        termPremium: '',
        permPremium: '',
        monthlyPayment: '',
        yearCoverage: '',
        permCarrier: '',
        termCarrier: '',
    });
    const [standaloneCustomerPortalData, setStandaloneCustomerPortalData] = useState<Customer | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!parameterStore?.approval?.emailAddress?.value) return;
        let cancelled = false;
        const fetchCustomerPortalData = async () => {
            setIsLoading(true);
            try {
                const data = await CustomerPortalAPI.GetCustomerPortalData(parameterStore.approval.emailAddress.value);
                if (!cancelled) {
                    setStandaloneCustomerPortalData(data?.customerByEmail ?? null);
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };
        fetchCustomerPortalData();
        // Boolean guard used because GetCustomerPortalData does not accept an AbortSignal
        return () => {
            cancelled = true;
        };
    }, [parameterStore?.approval?.emailAddress?.value]);

    useEffect(() => {
        if (!customerQuoteDataForEffect?.length) return;
        const quoteMap = new Map<string, string>();
        customerQuoteDataForEffect[0].v.properties.forEach((prop) => {
            quoteMap.set(prop.property ?? '', prop.value ?? '');
        });
        if (quoteMap.get('user_selected_category') === 'VUL') {
            setStandaloneCustomerSelected({
                policyType: quoteMap.get('user_selected_category') ?? '',
                cashValue: (quoteMap.get('vul') || 0).toString(),
                coverageAmount: '100000',
                monthlyPayment: quoteMap.get('contribution') ?? '',
                permCarrier: quoteMap.get('user_selected_vul_carrier') ?? '',
            });
        } else {
            setStandaloneCustomerSelected({
                policyType: quoteMap.get('selected_product_type') ?? '',
                cashValue: quoteMap.get('user_selected_cv_amt') ?? '',
                termCoverageAmount: quoteMap.get('user_selected_term_coverage') ?? '',
                permCoverageAmount: quoteMap.get('user_selected_perm_coverage') ?? '',
                termPremium: quoteMap.get('user_selected_term_premium') ?? '',
                permPremium: quoteMap.get('user_selected_perm_premium') ?? '',
                yearCoverage: quoteMap.get('term_period') ?? '',
                permCarrier: quoteMap.get('user_selected_perm_carrier') ?? '',
                termCarrier: quoteMap.get('user_selected_term_carrier') ?? '',
            });
        }
    }, [customerQuoteDataForEffect]);

    const standaloneWalkthrough = useMemo(() => deriveWalkthroughFromStore(parameterStore), [parameterStore]);

    const displayCustomerSelectedQuote = isStandalone ? standaloneCustomerSelectedQuote : props.customerSelectedQuote;
    const displayCustomerPortalData = isStandalone ? standaloneCustomerPortalData : props.customerPortalData;
    const displayWalkthroughProductType = isStandalone
        ? standaloneWalkthrough.walkthroughRecommendedProductType
        : props.walkthroughRecommendedProductType;
    const displayWalkthroughQuote = isStandalone
        ? standaloneWalkthrough.walkthroughRecommendedQuote
        : props.walkthroughRecommendedQuote;
    const displayUseProsperFlow = isStandalone ? standaloneWalkthrough.useProsperFlow : props.useProsperFlow;
    const displayUseIntentBuilderFlow = isStandalone
        ? standaloneWalkthrough.useIntentBuilderFlow
        : props.useIntentBuilderFlow;

    const hasAnyData = Boolean(displayCustomerSelectedQuote.policyType || displayWalkthroughProductType);
    const showClientSelected = Boolean(displayCustomerPortalData && displayCustomerSelectedQuote.policyType);

    const showLoading = isStandalone && isLoading && !hasAnyData;

    const dataItemStyle = showClientSelected ? {margin: 2, marginTop: 8} : {margin: 0, marginTop: 0};

    const displayClientSelectedProductType = displayUseIntentBuilderFlow
        ? displayCustomerSelectedQuote.policyType
        : displayUseProsperFlow
          ? 'IUL'
          : displayCustomerSelectedQuote.policyType;

    return (
        <div style={{paddingBottom: '8px'}}>
            {showLoading && (
                <div style={{padding: '12px 0', color: '#838383', fontSize: 14}}>Loading walkthrough data...</div>
            )}
            {!showLoading && !hasAnyData && (
                <div style={{padding: '12px 0', color: '#838383', fontSize: 14}}>
                    Walkthrough recommendation and client selection will appear here once available.
                </div>
            )}
            {hasAnyData && (
                <Grid container spacing={4}>
                    {displayWalkthroughProductType && (
                        <Grid item xs={showClientSelected ? 6 : 12}>
                            <CardContent className={classes.recommendationItem}>
                                <div>
                                    <strong className={classes.recommendationItemTitle}>Walkthrough Suggested </strong>
                                </div>
                                <div
                                    style={
                                        showClientSelected
                                            ? {}
                                            : {
                                                  display: 'grid',
                                                  gridTemplateColumns: 'repeat(3, 1fr)',
                                                  gap: '8px 24px',
                                                  marginTop: 8,
                                              }
                                    }
                                >
                                    <div style={dataItemStyle}>
                                        <strong>Product Type: </strong>
                                        {displayWalkthroughProductType}
                                    </div>
                                    <div style={dataItemStyle}>
                                        <strong>Carrier: </strong>
                                        {displayWalkthroughQuote?.carrier == 'EMC National Life'
                                            ? 'EMC National Life'
                                            : displayWalkthroughProductType == 'IUL'
                                              ? 'Non-Prosper Carrier'
                                              : displayWalkthroughQuote?.carrier}
                                    </div>
                                    <div style={dataItemStyle}>
                                        <strong>Coverage Amount:</strong> $
                                        {displayWalkthroughQuote?.faceAmount?.toLocaleString() || 'N/A'}
                                    </div>
                                    {displayWalkthroughQuote?.surrenderValue && (
                                        <div style={dataItemStyle}>
                                            <strong>Cash Value:</strong> $
                                            {displayWalkthroughQuote?.surrenderValue?.toLocaleString()}
                                        </div>
                                    )}
                                    <div style={dataItemStyle}>
                                        <strong>Monthly Payment: </strong>
                                        {getPremiumString(displayWalkthroughQuote?.modalPremium)}
                                    </div>
                                    {displayWalkthroughQuote?.termPeriod && (
                                        <div style={dataItemStyle}>
                                            <strong>Term Period:</strong> {displayWalkthroughQuote.termPeriod} yrs
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Grid>
                    )}
                    {displayCustomerPortalData && displayCustomerSelectedQuote.policyType && (
                        <Grid item xs={6}>
                            <CardContent className={classes.recommendationItem}>
                                <div>
                                    <strong className={classes.recommendationItemTitle}>Client Selected </strong>
                                </div>
                                <div style={{margin: 2, marginTop: 8}}>
                                    <strong>Product Type: </strong>
                                    {displayClientSelectedProductType}
                                </div>
                                {displayCustomerSelectedQuote.termCarrier && (
                                    <div style={{margin: 2, marginTop: 8}}>
                                        <strong>Carrier: </strong>
                                        {displayCustomerSelectedQuote.termCarrier}
                                    </div>
                                )}
                                {displayCustomerSelectedQuote.permCarrier && (
                                    <div style={{margin: 2, marginTop: 8}}>
                                        <strong>Carrier: </strong>
                                        {displayCustomerSelectedQuote.permCarrier == 'EMC National Life'
                                            ? 'EMC National Life'
                                            : displayClientSelectedProductType == 'IUL'
                                              ? 'Non-Prosper Carrier'
                                              : displayCustomerSelectedQuote.permCarrier}
                                    </div>
                                )}
                                {displayCustomerSelectedQuote.coverageAmount && (
                                    <div style={{margin: 2, marginTop: 8}}>
                                        <strong>Coverage Amount:</strong> $
                                        {Number(displayCustomerSelectedQuote.coverageAmount)?.toLocaleString()}
                                    </div>
                                )}
                                {displayCustomerSelectedQuote.termCoverageAmount && (
                                    <div style={{margin: 2, marginTop: 8}}>
                                        <strong>Term Coverage:</strong> $
                                        {Number(displayCustomerSelectedQuote.termCoverageAmount)?.toLocaleString()}
                                    </div>
                                )}
                                {displayCustomerSelectedQuote.permCoverageAmount && (
                                    <div style={{margin: 2, marginTop: 8}}>
                                        <strong>Perm Coverage:</strong> $
                                        {Number(displayCustomerSelectedQuote.permCoverageAmount)?.toLocaleString()}
                                    </div>
                                )}
                                {displayCustomerSelectedQuote.cashValue && (
                                    <div style={{margin: 2, marginTop: 8}}>
                                        <strong>Cash Value:</strong> $
                                        {Number(displayCustomerSelectedQuote.cashValue)?.toLocaleString()}
                                    </div>
                                )}
                                {displayCustomerSelectedQuote.monthlyPayment && (
                                    <div style={{margin: 2, marginTop: 8}}>
                                        <strong>Monthly Payment: </strong>
                                        {getPremiumString(Number(displayCustomerSelectedQuote.monthlyPayment))}
                                    </div>
                                )}
                                {displayCustomerSelectedQuote.termPremium && (
                                    <div style={{margin: 2, marginTop: 8}}>
                                        <strong>Term Premium: </strong>
                                        {getPremiumString(Number(displayCustomerSelectedQuote.termPremium))}
                                    </div>
                                )}
                                {displayCustomerSelectedQuote.permPremium && (
                                    <div style={{margin: 2, marginTop: 8}}>
                                        <strong>Perm Premium: </strong>
                                        {getPremiumString(Number(displayCustomerSelectedQuote.permPremium))}
                                    </div>
                                )}
                                {displayCustomerSelectedQuote.yearCoverage && (
                                    <div style={{margin: 2, marginTop: 8}}>
                                        <strong>Term Period:</strong> {displayCustomerSelectedQuote.yearCoverage} yrs
                                    </div>
                                )}
                            </CardContent>
                        </Grid>
                    )}
                </Grid>
            )}
        </div>
    );
};
